import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { alfredOnboardingEnabled } from './alfred-onboarding-flag';
import type { ConnectorStatus, ConnectorsStatus } from './connectors';
import { queryKeys } from './query-client';
import { useConnectConnector } from './use-connect-connector';
import { useOpenSideChat } from './use-open-side-chat';

/**
 * Alfred's Connect, in ONE place. The Outreach row's Connect and the settings screen's Connect and "Turn back
 * on" all run it, so where they go cannot drift apart. Mirrored by iOS and Android, where the row and the
 * settings screen share one helper the same way.
 *
 * `alfredOnboardingInSymphony` is read at the CLICK (warmed on mount): ON opens the onboarding
 * (`AlfredSetupModal`, rendered by the caller from `setupOpen`) — the screen that says a number is bought and
 * that calls cost credits, whose setup is idempotent and also switches a paused line back on; OFF is the chat
 * handoff through `useConnectConnector`, opened IN PLACE (`openChat: openSideChat`). `beforeChat` runs just
 * before that handoff, so a caller can arm `useSideChatFollow`.
 */
export function useAlfredConnect() {
  const queryClient = useQueryClient();
  const openSideChat = useOpenSideChat();
  const { busy, connect } = useConnectConnector({
    getConnectors: () => queryClient.getQueryData<ConnectorsStatus>(queryKeys.connectors)?.connectors ?? [],
    refresh: () => queryClient.invalidateQueries({ queryKey: queryKeys.connectors }),
    openChat: openSideChat,
  });
  const [setupOpen, setSetupOpen] = useState(false);

  useEffect(() => {
    void alfredOnboardingEnabled();
  }, []);

  const start = (connector: ConnectorStatus, beforeChat?: () => void) => {
    void alfredOnboardingEnabled().then((onboarding) => {
      if (onboarding) {
        setSetupOpen(true);
      } else {
        beforeChat?.();
        connect(connector);
      }
    });
  };

  // Alfred is a single connector, so "anything in flight" and "this row in flight" are the same thing.
  return { busy: busy.size > 0, start, setupOpen, closeSetup: () => setSetupOpen(false) };
}
